import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  AlertTriangle, 
  X, 
  Edit,
  MessageSquare,
  Send,
  User,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw
} from 'lucide-react';
import { formatDateTime } from '../utils/timeFormat';
import { useToast } from '../hooks/use-toast';

const IssueDetails = ({ 
  issue, 
  onClose, 
  onEdit, 
  onCloseIssue, 
  onReopenIssue,
  currentUser 
}) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Fetch comments for this issue
  const fetchComments = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/issues/${issue.id}/comments`);
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setComments(result.data);
        } else {
          console.error('Error fetching comments:', result.message);
          setComments([]);
        }
      } else {
        console.error('Error fetching comments:', response.statusText);
        setComments([]);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      setComments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (issue) {
      fetchComments();
    }
  }, [issue]);

  const handleAddComment = async (e) => {
    e.preventDefault();

    const trimmedComment = newComment.trim();

    if (!trimmedComment) {
      toast({
        title: "Error",
        description: "Comment cannot be empty",
        variant: "destructive",
      });
      return;
    }

    if (trimmedComment.length > 2000) {
      toast({
        title: "Error",
        description: "Comment is too long (max 2000 characters)",
        variant: "destructive",
      });
      return;
    }

    if (!currentUser) {
      toast({
        title: "Error",
        description: "You must be logged in to add comments",
        variant: "destructive",
      });
      return;
    }

    setIsSubmittingComment(true);

    try {
      const response = await fetch(`/api/issues/${issue.id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: trimmedComment,
          created_by: currentUser
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Add the new comment to the local state
        setComments(prev => [result.data, ...prev]);
        setNewComment('');

        toast({
          title: "Success",
          description: "Comment added successfully",
        });
      } else {
        // Handle specific error cases
        let errorMessage = result.message || 'Failed to add comment';

        if (response.status === 400) {
          if (errorMessage.includes('too long')) {
            errorMessage = 'Comment is too long (max 2000 characters)';
          } else if (errorMessage.includes('empty')) {
            errorMessage = 'Comment cannot be empty';
          }
        }

        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to add comment",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const getSeverityConfig = (severity) => {
    const configs = {
      critical: {
        label: 'Critical',
        color: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300',
        icon: AlertTriangle
      },
      medium: {
        label: 'Medium', 
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-300',
        icon: AlertTriangle
      },
      low: {
        label: 'Low',
        color: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300',
        icon: AlertTriangle
      }
    };
    return configs[severity] || configs.low;
  };

  if (!issue) return null;

  const severityConfig = getSeverityConfig(issue.severity);
  const SeverityIcon = severityConfig.icon;
  const isClosed = issue.status === 'closed';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="flex items-center gap-3 text-xl">
                <SeverityIcon className={`w-6 h-6 ${
                  issue.severity === 'critical' ? 'text-red-600' :
                  issue.severity === 'medium' ? 'text-yellow-600' :
                  'text-green-600'
                }`} />
                <span className={isClosed ? 'line-through text-slate-500' : ''}>
                  {issue.title}
                </span>
              </CardTitle>
              
              <div className="flex items-center gap-3 mt-3">
                <Badge className={severityConfig.color}>
                  <SeverityIcon className="w-3 h-3 mr-1" />
                  {severityConfig.label}
                </Badge>
                
                {isClosed ? (
                  <Badge variant="outline" className="text-green-700 border-green-200 dark:border-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Closed
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-blue-700 border-blue-200 dark:border-blue-800">
                    <Clock className="w-3 h-3 mr-1" />
                    Open
                  </Badge>
                )}
              </div>
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Issue Details */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              {/* Description */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className={`text-slate-700 dark:text-slate-300 whitespace-pre-wrap ${
                    isClosed ? 'line-through' : ''
                  }`}>
                    {issue.description}
                  </p>
                </CardContent>
              </Card>
              
              {/* Comments Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Comments ({comments.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Add Comment Form */}
                  {currentUser && (
                    <form onSubmit={handleAddComment} className="space-y-3">
                      <div>
                        <textarea
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder="Add a comment about this issue..."
                          rows={3}
                          className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-slate-100 resize-none"
                          disabled={isSubmittingComment}
                          maxLength={2000}
                        />
                        <div className="text-xs text-slate-500 mt-1 text-right">
                          {newComment.length}/2000 characters
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="submit"
                          disabled={!newComment.trim() || isSubmittingComment}
                          size="sm"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          {isSubmittingComment ? 'Adding...' : 'Add Comment'}
                        </Button>
                      </div>
                    </form>
                  )}
                  
                  {/* Comments List */}
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {loading ? (
                      <div className="text-center py-4 text-slate-500">
                        <RefreshCw className="w-4 h-4 animate-spin mx-auto mb-2" />
                        Loading comments...
                      </div>
                    ) : comments.length === 0 ? (
                      <div className="text-center py-4 text-slate-500">
                        <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        No comments yet. Be the first to comment!
                      </div>
                    ) : (
                      comments.map((comment) => (
                        <div key={comment.id} className="border border-slate-200 dark:border-slate-600 rounded-lg p-3">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-slate-500" />
                              <span className="font-medium text-sm text-slate-700 dark:text-slate-300">
                                {comment.created_by}
                              </span>
                            </div>
                            <div className="text-xs text-slate-500">
                              {formatDateTime(comment.created_at)}
                            </div>
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-wrap">
                            {comment.content}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Sidebar */}
            <div className="space-y-4">
              {/* Status & Actions */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button
                    onClick={onEdit}
                    size="sm"
                    className="w-full"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Issue
                  </Button>
                  
                  {isClosed ? (
                    <Button
                      onClick={() => onReopenIssue(issue)}
                      size="sm"
                      variant="outline"
                      className="w-full"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Reopen Issue
                    </Button>
                  ) : (
                    <Button
                      onClick={() => onCloseIssue(issue)}
                      size="sm"
                      variant="destructive"
                      className="w-full"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Close Issue
                    </Button>
                  )}
                </CardContent>
              </Card>
              
              {/* Metadata */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Created By</label>
                    <div className="flex items-center gap-2 text-sm">
                      <User className="w-4 h-4 text-slate-500" />
                      <span>{issue.created_by}</span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Created At</label>
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-slate-500" />
                      <span>{formatDateTime(issue.created_at)}</span>
                    </div>
                  </div>
                  
                  {issue.updated_by && (
                    <div>
                      <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Last Updated</label>
                      <div className="text-sm">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-500" />
                          <span>{issue.updated_by}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDateTime(issue.updated_at)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {isClosed && issue.closed_by && (
                    <div>
                      <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Closed By</label>
                      <div className="text-sm">
                        <div className="flex items-center gap-2 text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          <span>{issue.closed_by}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-green-600">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDateTime(issue.closed_at)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IssueDetails;